import React, { useRef, useState } from "react";
import { Sparkles, Loader2, Undo2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

/**
 * Textarea with an "Enhance with AI" button below.
 * After AI completes, the textarea is scrolled back into view so on mobile
 * the user immediately sees the polished result.
 */
export default function EnhanceableTextarea({
  value,
  onChange,
  section = "",
  fieldLabel = "",
  contextHint = "",
  entryType = "project",
  testId,
  placeholder,
  rows,
}) {
  const taRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [previous, setPrevious] = useState(null);

  const enhance = async () => {
    const text = (value || "").trim();
    if (!text) {
      toast.message("Type something first, then I can polish it.");
      return;
    }
    setBusy(true);
    setPrevious(value);
    try {
      const { data } = await api.post("/ai/enhance", {
        text,
        section,
        field_label: fieldLabel,
        context_hint: contextHint,
        entry_type: entryType,
      });
      onChange?.({ target: { value: data.text } });
      // After updating, bring the textarea back into view on mobile so the
      // user doesn't have to scroll up to read what changed.
      requestAnimationFrame(() => {
        if (taRef.current) {
          try {
            taRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
          } catch (_) {
            /* older browsers */
          }
        }
      });
      toast.success("Text polished", {
        description: "Click Undo to revert.",
        action: {
          label: "Undo",
          onClick: () => onChange?.({ target: { value: previous || text } }),
        },
      });
    } catch (e) {
      const d = e?.response?.data?.detail || "Enhance failed";
      toast.error(typeof d === "string" ? d : "Enhance failed");
    } finally {
      setBusy(false);
    }
  };

  const undo = () => {
    if (previous == null) return;
    onChange?.({ target: { value: previous } });
    setPrevious(null);
  };

  return (
    <div className="space-y-2">
      <textarea
        ref={taRef}
        className="rcw-textarea"
        value={value || ""}
        onChange={onChange}
        placeholder={placeholder}
        rows={rows}
        data-testid={testId}
      />
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={enhance}
          disabled={busy}
          className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full bg-amber-50 text-amber-700 hover:bg-amber-100 transition-colors text-xs font-semibold"
          data-testid={testId ? `${testId}-enhance` : undefined}
        >
          {busy ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <Sparkles className="h-3.5 w-3.5" />
          )}
          {busy ? "Polishing…" : "Enhance with AI"}
        </button>
        {previous != null && !busy ? (
          <button
            type="button"
            onClick={undo}
            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full bg-gray-100 text-gray-700 hover:bg-gray-200 text-xs font-medium"
          >
            <Undo2 className="h-3.5 w-3.5" /> Undo
          </button>
        ) : null}
      </div>
    </div>
  );
}
